import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useNotification } from '../../components/notifications/useNotification';
import FormButton from '../../components/FormButton/FormButton';
import FormField from '../../components/FormField/FormField';
import axios from 'axios';
import './ResetPasswordPage.css';
import { PASSWORD_RULES } from '../../utils/functions';

const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const notification = useNotification();
    const [searchParams] = useSearchParams();

    // Prevent the reset token from leaking via the Referer header when
    // the page loads external resources or the user navigates away.
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'referrer';
        meta.content = 'no-referrer';
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);

    const rawToken = searchParams.get('token') || '';
    // validate token format before sending — expected: 64-char hex string
    const token = /^[a-f0-9]{64}$/.test(rawToken) ? rawToken : '';

    const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.password) {
            newErrors.password = t('errorMessages.password-required');
        } else {
            const failed = PASSWORD_RULES.filter((r) => !r.test(formData.password));
            if (failed.length > 0) {
                newErrors.password = t('errorMessages.password-too-weak');
            }
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = t('rp-confirm-required');
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = t('rp-passwords-dont-match');
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            Object.values(newErrors).forEach((msg) => notification.showWarning(msg));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (!token) {
            notification.showError(t('rp-invalid-token'));
            return;
        }

        try {
            setLoading(true);
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/reset-password`,
                { token, newPassword: formData.password },
                { withCredentials: true }
            );
            setSuccess(true);
            notification.showSuccess(t('rp-success'));

            // Redirect to login after a short delay
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 400) {
                notification.showError(t('rp-invalid-token'));
            } else {
                notification.showError(t('errorMessages.unknown-error'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Password-strength indicator for UX
    const ruleResults = PASSWORD_RULES.map((rule) => ({
        ...rule,
        passed: rule.test(formData.password),
    }));

    return (
        <div className="rp">
            <div className="rp-card">

                {/* ── Top bar ── */}
                <div className="rp-topbar">
                    <button
                        className="rp-back-btn"
                        type="button"
                        onClick={() => navigate('/login')}
                        aria-label="Go back"
                    >
                        ←
                    </button>
                    <span className="rp-brand">{t('lp-brand')}</span>
                </div>

                {/* ── Header ── */}
                <div className="rp-header">
                    <h1 className="rp-title">{t('rp-title')}</h1>
                    <p className="rp-subtitle">{t('rp-subtitle')}</p>
                </div>

                {/* ── No token warning ── */}
                {!token && (
                    <div className="rp-warning">
                        <span className="rp-warning-icon">⚠️</span>
                        <p>{t('rp-invalid-token')}</p>
                    </div>
                )}

                {/* ── Success ── */}
                {success && (
                    <div className="rp-success">
                        <span className="rp-success-icon">✅</span>
                        <p>{t('rp-success-description')}</p>
                    </div>
                )}

                {/* ── Form ── */}
                {!success && token && (
                    <form className="rp-form" onSubmit={handleSubmit} noValidate>
                        <FormField
                            label={t('rp-new-password-label')}
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder={t('rp-new-password-placeholder')}
                            icon="🔒"
                            error={errors.password}
                            maxLength={128}
                            autoComplete="new-password"
                        />

                        {/* Password strength rules */}
                        {formData.password.length > 0 && (
                            <ul className="rp-rules">
                                {ruleResults.map((r) => (
                                    <li key={r.key} className={r.passed ? 'rp-rule--pass' : 'rp-rule--fail'}>
                                        {r.passed ? '✓' : '✗'} {t(r.i18nKey)}
                                    </li>
                                ))}
                            </ul>
                        )}

                        <FormField
                            label={t('rp-confirm-password-label')}
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            placeholder={t('rp-confirm-password-placeholder')}
                            icon="🔒"
                            error={errors.confirmPassword}
                            maxLength={128}
                            autoComplete="new-password"
                        />

                        <FormButton
                            type="submit"
                            fullWidth
                            loading={loading}
                            loadingText={t('sending')}
                        >
                            {t('rp-reset-btn')}
                        </FormButton>
                    </form>
                )}

                {/* ── Footer ── */}
                <div className="rp-footer">
                    <p>
                        {t('fp-remember-password')}
                        <Link to="/login">{t('fp-back-to-login')}</Link>
                    </p>
                </div>

            </div>
        </div>
    );
};

export default ResetPasswordPage;
